// Assembling the export workbooks and handing them to the browser.
//
// Thin on purpose — the decisions live in portfolioSheets.ts, which is pure and tested. This
// file is SheetJS calls and a download link.
//
// XLSX rather than CSV: the client works in Excel, the requirement arrived as a workbook, a
// CSV cannot carry three sheets, and dates and money can be written as real dates and
// numbers so Excel sorts and totals them without anyone reformatting a column. It costs no
// new dependency either — the same library already reads the extract on the Import page.

import * as XLSX from 'xlsx';
import type { PortfolioExport } from '@app/shared';
import { loanSheet, packageSheet, noteSheet, type Sheet } from './portfolioSheets';

/** Columns whose values should be written as real dates rather than text. */
const DATE_HEADER = /^(date|next due|maturity|purchase|submitted|closed)|date$/i;

function toWorksheet(sheet: Sheet): XLSX.WorkSheet {
  const ws = XLSX.utils.aoa_to_sheet([sheet.headers, ...sheet.rows]);

  // Excel sorts and filters a real date; it sorts '9/15/33' as text. The values arrive as
  // 'YYYY-MM-DD' from the API, so they are already unambiguous — this only tells Excel what
  // they are. Done by header name because the shape is built from data, not from a schema.
  sheet.headers.forEach((header, col) => {
    if (!DATE_HEADER.test(header)) return;
    for (let row = 1; row <= sheet.rows.length; row++) {
      const ref = XLSX.utils.encode_cell({ r: row, c: col });
      const cell = ws[ref];
      if (!cell || typeof cell.v !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(cell.v)) continue;
      cell.t = 'd';
      cell.v = new Date(`${cell.v}T00:00:00Z`);
      cell.z = 'm/d/yyyy';
    }
  });

  // Enough width to read without dragging every column. Notes is capped rather than sized to
  // its content: one three-year narrative would otherwise set the column to a screen wide.
  ws['!cols'] = sheet.headers.map(h => ({
    wch: Math.min(Math.max(h.length + 2, 12), h === 'Notes' ? 60 : 28),
  }));
  // Freeze the header row and turn on autofilter, since "poke around" means sort and filter.
  ws['!freeze'] = { xSplit: '0', ySplit: '1' };
  if (sheet.rows.length > 0) {
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range(
        { r: 0, c: 0 },
        { r: sheet.rows.length, c: Math.max(sheet.headers.length - 1, 0) },
      ),
    };
  }
  return ws;
}

function download(wb: XLSX.WorkBook, filename: string): void {
  // writeFile would do this in one call, but going through a Blob keeps the download an
  // ordinary object URL we revoke, rather than library-managed behaviour we cannot see.
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
  const url = URL.createObjectURL(new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const stamp = (iso: string) => iso.slice(0, 10);

/**
 * Report 1 — one sheet, one row per loan.
 *
 * The combined view: every column the model can fill, with the ones nothing has filled
 * pruned out. Notes are concatenated into their cell, as the client's own report has them.
 */
export function exportLoansWorkbook(data: PortfolioExport): void {
  const wb = XLSX.utils.book_new();
  const sheet = loanSheet(data.loans, data.notes, data.generatedAt);
  XLSX.utils.book_append_sheet(wb, toWorksheet(sheet), sheet.name);
  download(wb, `loans-${stamp(data.generatedAt)}.xlsx`);
}

/**
 * Report 2 — a sheet per entity: loans, packages, notes.
 *
 * The flat sheet can only carry the latest of each mirrored package date, so a loan with
 * three Extensions loses two of them. This is the shape that does not.
 */
export function exportPortfolioWorkbook(data: PortfolioExport): void {
  const wb = XLSX.utils.book_new();
  for (const sheet of [
    loanSheet(data.loans, data.notes, data.generatedAt),
    packageSheet(data.packages),
    noteSheet(data.notes),
  ]) {
    XLSX.utils.book_append_sheet(wb, toWorksheet(sheet), sheet.name);
  }
  download(wb, `portfolio-${stamp(data.generatedAt)}.xlsx`);
}
